import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { fetchCatBrands, type PrevBrandItem } from '@/api/catBrand';
import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyContentPanelInset, SurveyQuestionPanel } from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { readPrevBrandAnswers, writePrevBrandAnswers } from '@/lib/prevBrandStorage';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';

const rowBtn =
  'flex w-full items-center justify-between border-b border-black/35 bg-transparent px-2 py-1.5 text-left text-white transition hover:bg-black/15';

const variantBtn =
  'w-full border-b border-black/30 px-2 py-1.5 text-left text-[14px] transition sm:text-[15px]';

export default function PrevBrand() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet } = useAppSelector(selectOtpDraft);

  const [brands, setBrands] = useState<PrevBrandItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState(() => readPrevBrandAnswers()?.brand ?? '');
  const [selectedVariant, setSelectedVariant] = useState(() => readPrevBrandAnswers()?.variant ?? '');

  useEffect(() => {
    writePrevBrandAnswers({ brand: selectedBrand, variant: selectedVariant });
  }, [selectedBrand, selectedVariant]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setWarning(null);
    void (async () => {
      try {
        const list = await fetchCatBrands();
        if (cancelled) return;
        setBrands(list);
      } catch {
        if (!cancelled) setWarning(t.prevBrand.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t.prevBrand.loadError]);

  useEffect(() => {
    if (selectedBrand && !expandedBrand) {
      setExpandedBrand(selectedBrand);
    }
  }, [selectedBrand, expandedBrand]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  if (questionnaireSet === 'B') return <Navigate to="/ldproductnow" replace />;

  const variantsForExpanded = useMemo(
    () => brands.find((b) => b.brand === expandedBrand)?.variants ?? [],
    [brands, expandedBrand]
  );

  const handleSelectVariant = (brand: string, variant: string) => {
    setSelectedBrand(brand);
    setSelectedVariant(variant);
    setWarning(null);
  };

  const handleContinue = () => {
    if (!selectedBrand || !selectedVariant) {
      setWarning(t.prevBrand.requiredWarning);
      return;
    }
    writePrevBrandAnswers({ brand: selectedBrand, variant: selectedVariant });
    navigate('/survey/prevbrandanswer');
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto mt-[7vh] flex min-h-0 w-full max-w-full flex-1 flex-col">
          <SurveyContentPanelInset className="shrink-0">
            <SurveyQuestionPanel className="!mb-4">
              <p className="m-0 break-words text-left text-[20px] font-medium text-white sm:text-[21px]">
                {t.prevBrand.question}
              </p>
            </SurveyQuestionPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <p className="m-0 text-white/90">{t.prevBrand.loading}</p>
            ) : (
              <div className="flex flex-col gap-1">
                {brands.map((item) => {
                  const open = expandedBrand === item.brand;
                  return (
                    <div key={item.brand} className="bg-transparent">
                      <button
                        type="button"
                        className={rowBtn}
                        onClick={() => setExpandedBrand(open ? null : item.brand)}
                        aria-expanded={open}
                      >
                        <span className="font-semibold">{item.brand}</span>
                        <span className="text-lg leading-none">{open ? '▾' : '▸'}</span>
                      </button>
                      {open ? (
                        <div className="ml-2 mt-1 grid grid-cols-1 gap-1">
                          {variantsForExpanded.map((variant) => {
                            const selected = selectedBrand === item.brand && selectedVariant === variant;
                            return (
                              <button
                                key={variant}
                                type="button"
                                className={`${variantBtn} ${
                                  selected
                                    ? 'bg-white/20 text-white'
                                    : 'bg-transparent text-white/95'
                                }`}
                                onClick={() => handleSelectVariant(item.brand, variant)}
                              >
                                {variant}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-4 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {selectedBrand && selectedVariant ? (
                <p className="m-0 text-center text-sm text-white/90">
                  {selectedBrand} - {selectedVariant}
                </p>
              ) : null}
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton disabled={loading} onClick={handleContinue}>
                {t.prevBrand.continue}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
